import type { ThreadEntry } from "../../../api/threads/liveMessages";
import { selectedRangeIn } from "../../../shared/selection/selectedRange";
import { locatePassage, quotableText, type Passage } from "./passageQuote";

/** Marks the drawn text of an entry that may be quoted; its value is the entry's number. */
export const ENTRY_BODY_ATTRIBUTE = "data-entry-body";

/** A memory rests on at most this many passages (memory.md MEM-1). */
export const MAXIMUM_PASSAGES = 5;

export interface EntryPassage extends Passage {
  readonly entry: ThreadEntry;
}

export interface SelectedPassages {
  readonly passages: readonly EntryPassage[];
  /** Selected parts that cannot be quoted: other entries, tool results, text not in an entry. */
  readonly skipped: number;
  /** Where the selection ends, in the viewport, to place the action beside it. */
  readonly end: { readonly x: number; readonly y: number };
}

/** The part of `range` inside `element`, as text. */
function textWithin(range: Range, element: Element): string {
  const inside = document.createRange();
  inside.selectNodeContents(element);
  if (range.compareBoundaryPoints(Range.START_TO_START, inside) > 0) {
    inside.setStart(range.startContainer, range.startOffset);
  }
  if (range.compareBoundaryPoints(Range.END_TO_END, inside) < 0) {
    inside.setEnd(range.endContainer, range.endOffset);
  }
  return inside.toString();
}

/** How many times `selected` appears in the drawn entry before the selection starts. */
function appearancesBefore(range: Range, body: Element, selected: string): number {
  const before = document.createRange();
  before.selectNodeContents(body);
  if (range.compareBoundaryPoints(Range.START_TO_START, before) > 0) {
    before.setEnd(range.startContainer, range.startOffset);
  } else {
    return 0;
  }
  const drawn = body.textContent;
  const limit = before.toString().length;
  let count = 0;
  for (let at = drawn.indexOf(selected); at >= 0 && at < limit; at = drawn.indexOf(selected, at + 1)) {
    count += 1;
  }
  return count;
}

/**
 * What the person has selected in one Thread's log, as quotable passages of the entries it
 * touches (memory.md MEM-1: sources are quotes of one entry's text). Only a selection made in
 * `log` counts, and only `log`'s entries are read, with `entries`, that log's own: another
 * Thread beside it may show the same numbers and text (BA16-01). `null` when nothing is
 * selected there or nothing in it can be quoted.
 */
export function selectedPassages(log: Element | null, entries: ReadonlyMap<number, ThreadEntry>): SelectedPassages | null {
  const range = selectedRangeIn(log);
  if (log === null || range === null) {
    return null;
  }
  const passages: EntryPassage[] = [];
  let skipped = 0;
  for (const body of log.querySelectorAll(`[${ENTRY_BODY_ATTRIBUTE}]`)) {
    if (!range.intersectsNode(body)) {
      continue;
    }
    const selected = textWithin(range, body).trim();
    const entry = entries.get(Number(body.getAttribute(ENTRY_BODY_ATTRIBUTE)));
    if (selected === "" || entry === undefined) {
      continue;
    }
    const text = quotableText(entry);
    const found = text === null ? null : locatePassage(text, selected, appearancesBefore(range, body, selected));
    if (found?.status !== "found" || passages.length >= MAXIMUM_PASSAGES) {
      skipped += 1;
    } else {
      passages.push({ entry, ...found.passage });
    }
  }
  if (passages.length === 0) {
    return null;
  }
  const rects = range.getClientRects();
  const last = rects.item(rects.length - 1) ?? range.getBoundingClientRect();
  return { passages, skipped, end: { x: last.right, y: last.bottom } };
}
